// Offline photo queue — binary blobs in IndexedDB (pasture-pin plan Phase 2;
// the "S5" queue pwa-field-capture.md deferred twice, finally built).
//
// The JSON offline queue (offlineQueue.js) is localStorage/JSON-only and
// cannot carry camera blobs, so photos get their own IndexedDB store keyed
// by the observation's client_obs_id. The two queues are deliberately
// INDEPENDENT and order-free: the server adopts photos uploaded under
// entity_type='pin_observation' / entity_id=client_obs_id whether the JSON
// observation or the photos arrive first (routes/pasture_pins.py Phase 1).
//
// Flush is sequential in slot order (a, b, c) per observation so created_at
// order preserves slot intent even before the server's context-based slot
// matching kicks in. Failure classification mirrors offlineQueue's replay:
// network errors / 5xx / retryable codes BREAK the flush (item stays
// pending for the next online event); other 4xx quarantine the item (kept,
// surfaced in SyncStatus, retried only manually) — a photo is evidence, we
// never silently drop one. `photo-queue-changed` fires on every mutation so
// SyncStatus can live-update, matching `offline-queue-changed`.

const DB_NAME = 'wb-photo-queue'
const DB_VERSION = 1
const STORE = 'photos'
const RETRYABLE_STATUSES = new Set([401, 408, 425, 429])

function notifyChanged() {
  try {
    window.dispatchEvent(new CustomEvent('photo-queue-changed'))
  } catch { /* non-browser context */ }
}

const SLOT_ORDER = { a: 0, b: 1, c: 2, incursion: 3, voice: 4 }

function openDb() {
  return new Promise((resolve, reject) => {
    const req = indexedDB.open(DB_NAME, DB_VERSION)
    req.onupgradeneeded = () => {
      const db = req.result
      if (!db.objectStoreNames.contains(STORE)) {
        const store = db.createObjectStore(STORE, { keyPath: 'id', autoIncrement: true })
        store.createIndex('clientObsId', 'clientObsId', { unique: false })
      }
    }
    req.onsuccess = () => resolve(req.result)
    req.onerror = () => reject(req.error)
  })
}

function tx(db, mode, fn) {
  return new Promise((resolve, reject) => {
    const t = db.transaction(STORE, mode)
    const store = t.objectStore(STORE)
    const out = fn(store)
    t.oncomplete = () => resolve(out?.result !== undefined ? out.result : out)
    t.onerror = () => reject(t.error)
    t.onabort = () => reject(t.error)
  })
}

/**
 * Queue one photo blob for later upload.
 * @param {{clientObsId: string, slot: 'a'|'b'|'c'|'incursion'|'voice',
 *          ranchId: string, blob: Blob, filename?: string}} item
 */
export async function enqueuePhoto({ clientObsId, slot, ranchId, blob, filename }) {
  const db = await openDb()
  try {
    await tx(db, 'readwrite', (store) => store.add({
      clientObsId,
      slot,
      ranchId,
      blob,
      mime: blob.type || 'image/jpeg',
      filename: filename || `${clientObsId}-${slot}.jpg`,
      queuedAt: new Date().toISOString(),
      attempts: 0,
      quarantined: false,
      lastError: null,
    }))
  } finally {
    db.close()
  }
  notifyChanged()
}

async function allItems() {
  const db = await openDb()
  try {
    const items = await new Promise((resolve, reject) => {
      const t = db.transaction(STORE, 'readonly')
      const req = t.objectStore(STORE).getAll()
      req.onsuccess = () => resolve(req.result || [])
      req.onerror = () => reject(req.error)
    })
    return items
  } finally {
    db.close()
  }
}

/** Metadata (no blobs) for UI surfaces like SyncStatus. */
export async function listPhotoQueue() {
  const items = await allItems()
  return items.map(({ blob, ...meta }) => ({ ...meta, size: blob?.size ?? 0 }))
}

export async function pendingPhotoCount() {
  const items = await allItems()
  return items.filter((i) => !i.quarantined).length
}

export async function quarantinedPhotoCount() {
  const items = await allItems()
  return items.filter((i) => i.quarantined).length
}

async function removeItem(id) {
  const db = await openDb()
  try {
    await tx(db, 'readwrite', (store) => store.delete(id))
  } finally {
    db.close()
  }
}

async function updateItem(item) {
  const db = await openDb()
  try {
    await tx(db, 'readwrite', (store) => store.put(item))
  } finally {
    db.close()
  }
}

let flushing = false

/**
 * Upload every pending photo. `uploadFn(item)` must POST the blob (see
 * api.js `uploadPinPhoto`) and throw on failure. Sequential, slot-ordered
 * per observation. Returns {uploaded, failed, quarantined}.
 */
export async function flushPhotoQueue(uploadFn) {
  if (flushing) return { uploaded: 0, failed: 0, quarantined: 0, skipped: true }
  if (typeof navigator !== 'undefined' && !navigator.onLine) {
    return { uploaded: 0, failed: 0, quarantined: 0, skipped: true }
  }
  flushing = true
  try {
    const items = (await allItems())
      .filter((i) => !i.quarantined)
      .sort((x, y) =>
        x.clientObsId.localeCompare(y.clientObsId)
        || (SLOT_ORDER[x.slot] ?? 9) - (SLOT_ORDER[y.slot] ?? 9)
        || x.id - y.id)
    let uploaded = 0
    let failed = 0
    let quarantined = 0
    for (const item of items) {
      try {
        await uploadFn(item)
        await removeItem(item.id)
        uploaded += 1
      } catch (err) {
        failed += 1
        const status = err?.response?.status
        item.attempts += 1
        item.lastError = String(
          err?.response?.data?.detail || err?.message || err).slice(0, 300)
        // offlineQueue semantics: no response / 5xx / retryable codes stop
        // the flush and stay pending; any other 4xx is a permanent verdict
        // on THIS item -> quarantine, keep going.
        if (!status || status >= 500 || RETRYABLE_STATUSES.has(status)) {
          await updateItem(item)
          break
        }
        item.quarantined = true
        quarantined += 1
        await updateItem(item)
      }
    }
    return { uploaded, failed, quarantined }
  } finally {
    flushing = false
    notifyChanged()
  }
}

/** Retry a quarantined item (manual, from SyncStatus). */
export async function retryQuarantined(id) {
  const items = await allItems()
  const item = items.find((i) => i.id === id)
  if (!item) return
  item.quarantined = false
  item.attempts = 0
  item.lastError = null
  await updateItem(item)
  notifyChanged()
}

/** Discard a quarantined item (manual, explicit — evidence goes only when
 * a human says so). */
export async function discardPhoto(id) {
  await removeItem(id)
  notifyChanged()
}

/** Bind the flush to connectivity, mirroring installReplayListener. */
export function installPhotoFlushListener(uploadFn) {
  window.addEventListener('online', () => { flushPhotoQueue(uploadFn) })
  if (navigator.onLine) flushPhotoQueue(uploadFn)
}
